import type { Tag } from '@/api'

// Hints for cleaning up the tags. A tag that adds `C` directly and also adds `B`, where `B` adds `C` on its own (through any
// number of steps), has the direct link to `C` for nothing: the pull applies the whole chain.
export interface RedundantImplication {
  tag: Tag
  implied: Tag
  // The other directly added tags that already lead to `implied`.
  via: Tag[]
}

// Every tag id that `start` adds, directly or through others (`start` itself only if a loop leads back to it).
function reachable(byId: Map<number, Tag>, start: Tag): Set<number> {
  const seen = new Set<number>()
  const todo = [start]
  while (todo.length) {
    for (const link of todo.pop()!.implies) {
      if (seen.has(link.tag_id)) continue
      seen.add(link.tag_id)
      const next = byId.get(link.tag_id)
      if (next) todo.push(next)
    }
  }
  return seen
}

export function redundantImplications(tags: Tag[]): RedundantImplication[] {
  const byId = new Map(tags.map((tag) => [tag.id, tag]))
  const found: RedundantImplication[] = []
  for (const tag of tags) {
    if (tag.internal) continue
    const direct = tag.implies.map((link) => byId.get(link.tag_id)).filter((t): t is Tag => !!t)
    for (const implied of direct) {
      const via = direct.filter((other) => other.id !== implied.id && reachable(byId, other).has(implied.id))
      if (via.length) found.push({ tag, implied, via })
    }
  }
  const name = (t: Tag) => t.name.toLocaleLowerCase()
  return found.sort(
    (a, b) => name(a.tag).localeCompare(name(b.tag)) || name(a.implied).localeCompare(name(b.implied)),
  )
}
